import { RiLightbulbLine, RiGlobalLine, RiText } from 'react-icons/ri';
import GlassCard from '../ui/v2/GlassCard';

interface JobDescriptionInputProps {
    mode: 'text' | 'url';
    onModeChange: (mode: 'text' | 'url') => void;
    textValue: string;
    onTextChange: (value: string) => void;
    urlValue: string;
    onUrlChange: (value: string) => void;
    onUseSample: () => void;
    currentLength: number;
    maxLength?: number;
}

export default function JobDescriptionInput({
    mode,
    onModeChange,
    textValue,
    onTextChange,
    urlValue,
    onUrlChange,
    onUseSample,
    currentLength,
    maxLength = 5000
}: JobDescriptionInputProps) {
    return (
        <GlassCard className="h-full flex flex-col">
            <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold text-white">Job Description</h2>

                {mode === 'text' && (
                    <button
                        onClick={onUseSample}
                        className="flex items-center gap-1.5 text-xs font-medium text-cyan-400 hover:text-cyan-300 transition-colors"
                    >
                        <RiLightbulbLine size={16} />
                        <span>Use Sample JD</span>
                    </button>
                )}
            </div>

            {/* Mode Switcher */}
            <div className="flex p-1 bg-white/5 rounded-lg border border-white/5 mb-4">
                <button
                    onClick={() => onModeChange('text')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'text'
                            ? 'bg-white text-black shadow-lg shadow-white/10'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                >
                    <RiText size={16} />
                    Paste Text
                </button>
                <button
                    onClick={() => onModeChange('url')}
                    className={`flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all ${mode === 'url'
                            ? 'bg-white text-black shadow-lg shadow-white/10'
                            : 'text-gray-400 hover:text-white hover:bg-white/5'
                        }`}
                >
                    <RiGlobalLine size={16} />
                    Link URL
                </button>
            </div>

            <div className="flex-1 flex flex-col gap-4">
                {mode === 'text' ? (
                    <div className="relative flex-1">
                        <textarea
                            value={textValue}
                            onChange={(e) => onTextChange(e.target.value)}
                            placeholder="Paste the job description here...&#10;&#10;We are looking for a Senior Software Engineer to join our team. You will be responsible for designing and implementing scalable web applications..."
                            className="w-full h-full p-4 rounded-xl bg-white/5 border border-white/5 focus:border-blue-500/50 focus:bg-white/10 text-gray-200 placeholder:text-gray-600 resize-none outline-none transition-all duration-300 text-sm leading-relaxed custom-scrollbar"
                        />
                        <div className="absolute bottom-4 right-4 text-xs font-medium text-gray-600 pointer-events-none">
                            {currentLength} / {maxLength} characters
                        </div>
                    </div>
                ) : (
                    <div className="flex flex-col gap-4 h-full">
                        <div className="flex-1 flex flex-col justify-center items-center p-8 border border-dashed border-white/10 rounded-xl bg-white/[0.02]">
                            <div className="w-full max-w-md space-y-4">
                                <div className="text-center">
                                    <h3 className="text-lg font-medium text-white mb-2">Import from URL</h3>
                                    <p className="text-sm text-gray-400">
                                        Paste the link to the LinkedIn job post or company career page.
                                    </p>
                                </div>
                                <div className="relative">
                                    <RiGlobalLine className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
                                    <input
                                        type="url"
                                        value={urlValue}
                                        onChange={(e) => onUrlChange(e.target.value)}
                                        placeholder="https://linkedin.com/jobs/view/..."
                                        className="w-full pl-10 pr-4 py-3 rounded-lg bg-black/40 border border-white/10 focus:border-blue-500/50 text-white placeholder:text-gray-600 outline-none transition-all"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </GlassCard>
    );
}
